import shrinkImage from '../utils/shrinkImage'

/**
 * What every upload in ProTrack does on a weak connection, in one place.
 *
 * Two things, applied to any request carrying a FormData body:
 *
 *  1. SHRINK THE IMAGES. A phone photo is 3–8 MB; the same page re-encoded is a few hundred
 *     kilobytes and reads just as well, to a person and to the extractors. On the connections
 *     people in the field actually have, that is the difference between ten minutes and ten
 *     seconds — and it is also the difference between clearing the gateway's body limit and a
 *     413 nobody can act on.
 *
 *  2. REPORT PROGRESS. An upload with no feedback looks like a hang, and the honest response to
 *     a hang is to refresh — which is precisely how twenty minutes of signup was lost. Every
 *     upload now publishes a percentage that the indicator shows, wherever it was started from.
 *
 * Attached to the shared clients rather than to each form: there are more than twenty places
 * that post a file, and a rule applied in twenty places is a rule that holds in nineteen.
 */

// ── progress, published to whoever is listening ──────────────────────────────
const listeners = new Set()
let state = { active: 0, percent: 0 }

const publish = () => listeners.forEach(fn => fn(state))

export function onUploadState(fn) {
  listeners.add(fn)
  fn(state)
  return () => listeners.delete(fn)
}

const startUpload = () => { state = { ...state, active: state.active + 1, percent: 0 }; publish() }
const setPercent = (p) => { state = { ...state, percent: p }; publish() }
const endUpload = () => {
  const active = Math.max(0, state.active - 1)
  state = { active, percent: active ? state.percent : 0 }
  publish()
}

// ── the interceptor both clients share ───────────────────────────────────────
const isFormData = (v) => typeof FormData !== 'undefined' && v instanceof FormData

async function shrinkFormData(fd) {
  const out = new FormData()
  let changed = false
  for (const [key, value] of fd.entries()) {
    if (typeof File !== 'undefined' && value instanceof File && value.type?.startsWith('image/')) {
      const smaller = await shrinkImage(value)
      if (smaller !== value) changed = true
      out.append(key, smaller, smaller.name)
    } else {
      out.append(key, value)
    }
  }
  return changed ? out : fd
}

export function attachUploadHandling(client) {
  client.interceptors.request.use(async (config) => {
    if (!isFormData(config.data)) return config

    try {
      config.data = await shrinkFormData(config.data)
    } catch {
      // Shrinking is a speed-up, never a precondition. Send what we were given.
    }

    startUpload()
    const theirs = config.onUploadProgress
    config.onUploadProgress = (e) => {
      if (e.total) setPercent(Math.round((e.loaded * 100) / e.total))
      theirs?.(e)          // a caller with its own progress bar keeps it
    }
    config.__tracked = true
    return config
  })

  const finish = (configLike) => { if (configLike?.__tracked) endUpload() }
  client.interceptors.response.use(
    (response) => { finish(response.config); return response },
    (error) => { finish(error.config); return Promise.reject(error) },
  )
}
